import { Injectable } from "@angular/core";
import {
	GeoFraudDetailsBody,
	PlatformServiceCommand,
	PlatformServiceValue,
	GeoFraudDetailsResponse,
	GeolocationCustomErrorType,
} from "@fabric-types/svc-platform";
import { DocumentRef, FeatureFlagService } from "@obg/ngx.global";
import { iif, Observable, throwError } from "rxjs";
import { catchError, switchMap } from "rxjs/operators";

import { AppError } from "../app-error/app-error.model";

import { NOT_SUPPORTED, POSITION_OPTIONS } from "./geolocation.const";
import { GeoFraudDetails } from "./geolocation.model";
import { GeolocationProvider } from "./geolocation.provider";

@Injectable({ providedIn: "root" })
export class GeolocationService {
	constructor(
		private geolocationProvider: GeolocationProvider,
		private docRef: DocumentRef,
		private featureFlagService: FeatureFlagService
	) {}

	// to allow or deny the location popup
	getPosition$(): Observable<GeolocationPosition> {
		return new Observable<GeolocationPosition>((subscriber) => {
			if (!this.geolocationProvider.isSupported()) {
				subscriber.error(NOT_SUPPORTED);
				return;
			}

			// Following works only in HTTPS sites:
			this.geolocationProvider.get().getCurrentPosition(
				(position: GeolocationPosition) => {
					subscriber.next(position);
					subscriber.complete();
				},
				(error) => {
					// Brazil has a different structure than argentina which includes the keyword "data"
					subscriber.error({ code: error.code, message: error.message, data: { code: error.code } });
					return;
				},
				POSITION_OPTIONS
			);
		});
	}

	checkIfTheUserIsFraud$(
		authType: "REGISTER" | "LOGIN" = "REGISTER",
		customerId?: string
	): Observable<GeoFraudDetails> {
		return new Observable(
			this.docRef.obgEventBus.retrieve(
				PlatformServiceValue.RadarUserFraudDetails,
				customerId ? { guid: customerId } : null
			)
		).pipe(
			switchMap((payload) => {
				if (payload.error) {
					return throwError(() => payload.error);
				}
				return this.grantPassingObservable$({
					token: payload.token,
					deviceId: payload.deviceId,
					action: authType,
					provider: "Radar",
					customerId,
				});
			}),
			catchError((error: GeolocationCustomErrorType) => {
				return iif(
					() => authType === "REGISTER",
					throwError(() => {
						if ("messageType" in error && "messageCode" in error) {
							return error.messageType;
						}
						return error;
					}),
					throwError(() => {
						if ("messageType" in error && "messageCode" in error) {
							const messageCode = error.messageCode.replace(".", "-");
							const localError: AppError = {
								code: messageCode,
								type: error.code,
								data: { code: messageCode },
							};

							return localError;
						}

						return error;
					})
				);
			})
		);
	}

	private grantPassingObservable$(payload: GeoFraudDetailsBody): Observable<GeoFraudDetails> {
		return new Observable<GeoFraudDetails>((observer) => {
			this.docRef.obgEventBus?.dispatch(PlatformServiceCommand.GeolocationGrantPassing, {
				body: {
					action: payload.action,
					deviceId: payload.deviceId ?? "",
					token: payload.token ?? "",
					provider: payload.provider,
					customerId: payload.customerId,
				},
				onSuccess: (response: GeoFraudDetailsResponse) => {
					observer.next({ isFraud: response.isFraud, reason: response.failedReason[0] ?? "unknown" });
					observer.complete();
				},
				onError: (error: unknown) => {
					observer.error(error);
				},
			});
		});
	}

	setGeolocationGuid(guid: string): void {
		this.docRef.obgEventBus?.dispatch(PlatformServiceCommand.GeolocationSetGuid, {
			guid,
		});
	}

	stopGeolocationTracking(): void {
		this.docRef.obgEventBus?.dispatch(PlatformServiceCommand.GeolocationStopTracking);
	}

	showGeofencingErrorAlert(): void {
		this.docRef.obgEventBus?.dispatch(PlatformServiceCommand.GeolocationShowGeofencingErrorAlert);
	}

	isBrazilGeofencingEnabled(): boolean {
		return !!this.featureFlagService.get<{ enabled: boolean | undefined }>("brazil_geolocation")?.enabled;
	}
}
